
import React, { useState, useContext } from 'react'
import {
	Button,
	Card,
	CardBody,
	CardGroup,
	Col,
	Form,
	FormGroup,
	Input,
	InputGroup,
	InputGroupText,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'
import classnames from 'classnames'
import { library } from "@fortawesome/fontawesome-svg-core"
import { faHandHoldingDollar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

library.add(faHandHoldingDollar)

import { trigger } from '../../events/customEvents'
import AuctionsService from '../../services/AuctionsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'
import asDescribedImage from '../../assets/images/brand/as-described.png'
import conditionReportImage from '../../assets/images/brand/condition-report.png'
import firstRunImage from '../../assets/images/brand/first-run.png'

const PlaceMemberBidModal = ({ showMemberBidModal, auctionListItem, }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const { t } = useTranslation()
	const [focusedBidAmount, setFocusedBidAmount] = useState(false)
	const [memberBid, setMemberBid] = useState({ auctionTierMemberId: undefined, bidAmount: 0.00, validation: { bidAmount: false, auctionTierMemberId: false } })
	const sessionContext = useContext(SessionContext)
	const [bidSubmitted, setBidSubmitted] = useState(false)
	const [validationMessage, setValidationMessage] = useState('')

	const handleOnOpened = () =>
	{
		if (auctionListItem)
		{
			let bidAmount = auctionListItem.startPrice
			if (auctionListItem.highestBidAmount && auctionListItem.highestBidAmount > 0)
			{
				bidAmount = auctionListItem.highestBidAmount + 100
			}

			setMemberBid((previousMemberBid) =>
			{
				let currentMemberBid = {...previousMemberBid}
				currentMemberBid.auctionTierMemberId = auctionListItem.auctionTier.auctionTierMemberId
				currentMemberBid.validation.auctionTierMemberId = true
				currentMemberBid.bidAmount = bidAmount
				currentMemberBid.validation.bidAmount = true
				return currentMemberBid
			})
		}
	}

	const handleCancelModal = (e) =>
	{
		e.stopPropagation()
		trigger('placeMemberBidModalClosed', { bidPlaced: false, reason: 'Cancelled', bidAmount: 0 })
	}

	const handleBidAmountChange = (e) =>
	{
		const { name, value} = e.target
		setMemberBid((previousMemberBid) =>
		{
			let currentMemberBid = {...previousMemberBid}
			currentMemberBid[name] = value
			currentMemberBid.validation[name] = (value && !isNaN(value) && parseFloat(value) > 0)

			currentMemberBid.auctionTierMemberId = auctionListItem.auctionTier.auctionTierMemberId
			currentMemberBid.validation.auctionTierMemberId = true

			return currentMemberBid
		})
	}

	const validateBid = () =>
	{
		const bidValidation =
		{
			validBid: false,
			validationMessage: 'Your bid must be greater than the start price.'
		}

		if (memberBid.validation.bidAmount && memberBid.validation.auctionTierMemberId)
		{
			if (auctionListItem.auctionTier.yourBid && (auctionListItem.auctionTier.yourBid.bidAmount === auctionListItem.highestBidAmount))
			{
				bidValidation.validBid = false
				bidValidation.validationMessage = `Your bid must be greater than the highest bid amount of ${ auctionListItem.highestBidAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }.`
			}
			else
			{
				if (auctionListItem.highestBidAmount && auctionListItem.highestBidAmount > 0)
				{
					if (memberBid.bidAmount >= (auctionListItem.highestBidAmount + 100))
					{
						bidValidation.validBid = true
					}
					else
					{
						bidValidation.validBid = false
						bidValidation.validationMessage = 'Bidding increments are a minimum of $100'
					}
				}
				else if (memberBid.bidAmount)
				{
					if (memberBid.bidAmount >= auctionListItem.startPrice)
					{
						bidValidation.validBid = true
					}
					else
					{
						bidValidation.validBid = false
						bidValidation.validationMessage = 'Your bid must be greater than the start price.'
					}
				}
			}
		}

		return bidValidation
	}

	const handlePlaceBid = (e) =>
	{
		e.stopPropagation()

		setBidSubmitted(true)
		const bidValidation = validateBid()
		if (bidValidation.validBid)
		{
			setIsModalBusy(true)
			AuctionsService
				.addMemberBid(auctionListItem.auctionTier.auctionTierMemberId, memberBid.bidAmount, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setBidSubmitted(false)
					setIsModalBusy(false)
					sessionContext.updateSession(result.data.loginSession)

					trigger('raiseNotification', { title: 'Add Success', message: 'The bid was placed.', type: 'success', duration: 5 })
					auctionListItem.auctionTier.yourBid = result.data.actionResult

					trigger('placeMemberBidModalClosed', { bidPlaced: true, reason: '', bidAmount: memberBid.bidAmount })
				})
				.catch((error) =>
				{
					setBidSubmitted(false)
					setIsModalBusy(false)
					SiteService.displayApiError(error)
				})
		}
		else
		{
			trigger('raiseNotification', { title: 'Invalid Bid', message: bidValidation.validationMessage, type: 'danger', duration: 5 })
		}
	}

	return (
		<>
			<Modal
				isOpen={ showMemberBidModal }
				onOpened={ handleOnOpened }
				toggle={ (event) => handleCancelModal(event) }
				size="lg"
				fullscreen="md"
			>
				<SpinnerComponent
					loading={ isModalBusy }
					position="centered"
					color="#3578e5"
					secondaryColor="#f3f3f3"
					backgroundColor="#d3d3d3"
					spinnerType="circle-dots"
				/>
				<ModalHeader toggle={ (event) => handleCancelModal(event) } charCode="Y">
					{ t('Modals.PlaceMemberBid.Title') }
				</ModalHeader>
				<ModalBody>
					<Card >
						<CardBody>
							{
								(auctionListItem !== undefined) &&
								<Row className="align-items-center" style={{ marginLeft: '5px', marginRight: '5px' }}>
									<Col md="8" style={{ padding: '0' }}>
										<Row>
											<Col md="4">
												<img className="inspection-pic-sm" src={ auctionListItem.primaryDisplayImageUrl}  alt="..." width="127" height="95" />
											</Col>
											<Col md="8" className="text-primary">
												<div style={{ marginLeft: '5px' }}><label style={{ fontSize: '12px' }}>{auctionListItem.year + ' ' + auctionListItem.make + ' ' + auctionListItem.model}</label></div>
												<div style={{ marginLeft: '5px' }}><label style={{ fontSize: '12px' }}>{'Int: ' + auctionListItem.interiorColor + ' | ' + SiteService.numberDisplay(auctionListItem.cylinders, 0) + ' Cyl | ' + auctionListItem.displacement + ' | ' + auctionListItem.drivetrain + ' ' + auctionListItem.serialNumber}</label></div>
												<div style={{ marginLeft: '5px' }}>
													<img alt="..." style={{ display: 'inline-block', marginRight: '5px' }} height="24px" width="24px" src={ asDescribedImage } />
													<img alt="..." style={{ display: 'inline-block',marginRight: '5px'}} height="24px" width="24px" src={ conditionReportImage } />
													<img alt="..." style={{ display: 'inline-block', marginRight: '5px' }} height="24px" width="24px" src={ firstRunImage } />
												</div>
											</Col>
										</Row>
									</Col>
									<Col md="4" style={{ padding: '0' }}>
										<CardGroup style={{ minHeight:'140', height: '140' }}>
											<Card style={{ borderStyle: 'solid', borderWidth: '1px', backgroundColor: '#f6f9fc' }}>
												<CardBody style={{ padding: '12px' }}>
													<Col md="12" className="text-primary" >
														<div>
															<label style={{ marginRight:'5px', fontSize: '12px' }}>{ t('Auctions.Listing.Start') + ': ' }</label>
															<label style={{ fontSize: '12px' }}>{ SiteService.priceDisplay(auctionListItem.startPrice) }</label>
														</div>
														<div>
															<label style={{ marginRight:'5px', fontSize: '12px' }}>{ t('Modals.PlaceMemberBid.Highest_Bid') + ':' }</label>
															<label style={{ fontSize: '12px' }}>{ SiteService.priceDisplay(auctionListItem.highestBidAmount) }</label>
														</div>
														<div>
															<label style={{ marginRight:'5px', fontSize: '12px' }}>{ t('Modals.PlaceMemberBid.Your_Bid') + ':' }</label>
															<span style={{ fontSize: '12px' }}>{ (auctionListItem.auctionTier && auctionListItem.auctionTier.yourBid) ? SiteService.priceDisplay(auctionListItem.auctionTier.yourBid.bidAmount) : '' }</span>
														</div>
														<div><label style={{ marginRight:'5px', fontSize: '12px' }}><span style={{ fontSize: '12px', marginRight: '5px' }}>{ SiteService.numberDisplay(auctionListItem.totalMembersWatching) }</span>{ t('Modals.PlaceMemberBid.Currently_Watching') }</label></div>
													</Col>
												</CardBody>
											</Card>
										</CardGroup>
									</Col>
								</Row>
							}
							{ ((auctionListItem && auctionListItem.auctionTier) && (!auctionListItem.auctionTier.yourBid || (auctionListItem.auctionTier.yourBid && auctionListItem.auctionTier.yourBid.bidAmount < auctionListItem.highestBidAmount))) &&
								<Form role="form" style={{ marginTop:'20px' }}>
									<FormGroup className={classnames("mb-3", { focused: focusedBidAmount, })}>
										<InputGroup className="input-group-merge input-group-alternative">
											<InputGroupText>
												<FontAwesomeIcon icon={ faHandHoldingDollar } />
											</InputGroupText>
											<Input
												autoFocus
												name="bidAmount"
												placeholder="Bid Amount"
												type="text"
												onFocus={() => setFocusedBidAmount(true)}
												onBlur={() => setFocusedBidAmount(false)}
												value={ memberBid.bidAmount }
												onChange={ handleBidAmountChange }
												valid={ bidSubmitted && memberBid.validation.bidAmount }
												invalid={ bidSubmitted && !memberBid.validation.bidAmount }
											/>
										</InputGroup>
									</FormGroup>
								</Form>
							}
						</CardBody>
					</Card>
				</ModalBody>
				<ModalFooter>
					<Row style={{ width: '100%' }} >
						<Col>
							<Button
								block
								color="secondary"
								size="sm"
								onClick={(event) => handleCancelModal(event)}
							>
								{ t('Modals.PlaceMemberBid.Cancel') }
							</Button>
						</Col>
						<Col>
							{
								SiteService.isDealerPlaceBidVisible(auctionListItem) &&
								<Button
									block
									color="primary"
									size="sm"
									onClick={(event) => handlePlaceBid(event)}
									disabled={ auctionListItem && auctionListItem.hasOwnProperty('secondsRemaining') && auctionListItem.secondsRemaining < 1 }
								>
									{ t('Modals.PlaceMemberBid.Place_Bid') }
								</Button>
							}
						</Col>
					</Row>
				</ModalFooter>
			</Modal>

		</>
	)
}

export default PlaceMemberBidModal
